import { useState, useEffect, useRef } from "react";

import { useDidMountEffect } from "../../utils/utils";

export default function useUsers(
  apiClient,
  endpoint,
  updateInterval = 1500,
  isFocused = true,
) {
  const [users, setUsers] = useState(null);

  // pagination
  const [batch, setBatch] = useState(0);
  const [nPages, setNPages] = useState(null);
  const batchSize = 20;

  // search filter
  const [searchInput, setSearchInput] = useState("");

  const initialized = useRef(false);
  const interval = useRef(null);

  useEffect(() => {
    if (isFocused) initData();
    else clearData();
  }, [isFocused]);

  useEffect(() => {
    if (!users) return; // run if 'searchInput' changes, but not on initial render
    const delayDebounceFn = setTimeout(() => {
      if (batch !== 0) setBatch(0);
      else getUsers();
    }, 1500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchInput]);

  useDidMountEffect(() => {
    // run if 'batch' changes, but not on initial render
    loadData();
  }, [batch]);

  const getUsers = async () => {
    const params = {
      batch_size: batchSize,
      batch,
      search: searchInput || null,
      order_by: "-id",
    };
    const response = await apiClient.get(endpoint, params);
    setNPages(Number(response.headers.length));
    setUsers(response.data);
  };

  const loadData = getUsers;
  const initData = () => {
    if (batch === 0) loadData();
    else setBatch(0);
    initialized.current = true;
  };
  const clearData = () => {
    setUsers(null);
    initialized.current = false;
  };

  const startRecurrentUpdate = (intervalTime) => {
    if (!interval.current)
      interval.current = setInterval(() => loadData(), intervalTime);
  };

  const stopRecurrentUpdate = () => {
    if (interval.current) {
      clearInterval(interval.current);
      interval.current = null;
    }
  };

  useEffect(() => {
    if (initialized.current && updateInterval) {
      startRecurrentUpdate(updateInterval);
      return stopRecurrentUpdate; // This represents the unmount function, in which you need to clear your interval to prevent memory leaks.
    }
    if (!initialized.current) stopRecurrentUpdate();
  }, [initialized.current]);

  return {
    users,
    pagination: { nPages, page: batch, setPage: setBatch },
    search: { input: searchInput, setInput: setSearchInput },
    data: { update: loadData, clear: clearData, init: initData },
    recurrentUpdate: { start: startRecurrentUpdate, stop: stopRecurrentUpdate },
  };
}
